import { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import { rosterTheme as t } from "./rosterTheme";
import {
  canEditRosterMetadata,
  findRosterForPeriod,
  periodFromMonthYear,
  rosterStatusLabel,
  validateRosterCreate,
} from "../../Moduels/attendance/rosterValidation";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function RosterCreateModal({
  open,
  onClose,
  onCreate,
  onUpdate,
  onOpenExisting,
  rosters = [],
  editRoster = null,
}) {
  const now = new Date();
  const isEdit = Boolean(editRoster);
  const [form, setForm] = useState({
    name: "",
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    description: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    unit_label: "",
    roster_type: "monthly",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const period = useMemo(
    () => periodFromMonthYear(form.month, form.year),
    [form.month, form.year]
  );

  const suggestedName = `Roster ${MONTHS[form.month - 1]} ${form.year}`;

  const duplicateRoster = useMemo(
    () => (isEdit ? null : findRosterForPeriod(rosters, period.period_start)),
    [isEdit, rosters, period.period_start]
  );

  useEffect(() => {
    if (!open) return;
    if (editRoster) {
      const start = new Date(`${editRoster.period_start}T00:00:00`);
      setForm({
        name: editRoster.name || "",
        month: start.getMonth() + 1,
        year: start.getFullYear(),
        description: editRoster.description || "",
        timezone: editRoster.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        unit_label: editRoster.unit_label || "",
        roster_type: editRoster.roster_type || "monthly",
      });
    } else {
      setForm({
        name: suggestedName,
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        description: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        unit_label: "",
        roster_type: "monthly",
      });
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editRoster]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateRosterCreate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!isEdit && duplicateRoster) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || suggestedName,
        ...period,
        description: form.description || null,
        timezone: form.timezone,
        unit_label: form.unit_label || null,
        roster_type: form.roster_type,
      };
      if (isEdit) {
        await onUpdate(editRoster.roster_plan_id, {
          name: payload.name,
          description: payload.description,
          timezone: payload.timezone,
          unit_label: payload.unit_label,
          roster_type: payload.roster_type,
        });
      } else {
        await onCreate(payload);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const metadataEditable = !isEdit || canEditRosterMetadata(editRoster);

  return (
    <ModalShell
      open={open}
      title={isEdit ? "Edit roster" : "Create roster"}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={t.label}>Roster name</label>
          <input
            className={t.input}
            value={form.name}
            disabled={!metadataEditable}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={suggestedName}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={t.label}>Month</label>
            <select
              className={t.select}
              value={form.month}
              disabled={isEdit}
              onChange={(e) => {
                const month = Number(e.target.value);
                setForm({
                  ...form,
                  month,
                  name: form.name === suggestedName || !form.name
                    ? `Roster ${MONTHS[month - 1]} ${form.year}`
                    : form.name,
                });
              }}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={t.label}>Year</label>
            <input
              type="number"
              className={t.input}
              value={form.year}
              disabled={isEdit}
              onChange={(e) => {
                const year = Number(e.target.value);
                setForm({
                  ...form,
                  year,
                  name: form.name === suggestedName || !form.name
                    ? `Roster ${MONTHS[form.month - 1]} ${year}`
                    : form.name,
                });
              }}
            />
          </div>
        </div>
        <div className={t.periodBox}>
          Period: <strong className="text-slate-800">{period.period_start}</strong>
          {" → "}
          <strong className="text-slate-800">{period.period_end}</strong>
        </div>
        {errors.period && <p className="text-xs text-red-600">{errors.period}</p>}

        {!isEdit && duplicateRoster && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-medium">Roster already exists for this month</p>
            <p className="mt-1 text-amber-900">
              {duplicateRoster.name} · {rosterStatusLabel(duplicateRoster.status)} · v
              {duplicateRoster.version}
            </p>
            <p className="mt-1 text-amber-800">
              Only one roster is allowed per month. Open or edit the existing roster instead.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className={t.btnPrimary}
                onClick={() => onOpenExisting?.(duplicateRoster)}
              >
                Open roster
              </button>
              {canEditRosterMetadata(duplicateRoster) && (
                <button
                  type="button"
                  className={t.btnSecondary}
                  onClick={() => onOpenExisting?.(duplicateRoster, { edit: true })}
                >
                  Edit roster
                </button>
              )}
            </div>
          </div>
        )}

        {isEdit && !metadataEditable && (
          <p className="text-sm text-slate-600">
            This roster is {rosterStatusLabel(editRoster.status).toLowerCase()} and its details
            cannot be changed. You can still manage shift assignments from the planner.
          </p>
        )}

        <div>
          <label className={t.label}>Description</label>
          <textarea
            className={t.input}
            rows={2}
            value={form.description}
            disabled={!metadataEditable}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={t.label}>Timezone</label>
            <input
              className={t.input}
              value={form.timezone}
              disabled={!metadataEditable}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </div>
          <div>
            <label className={t.label}>Roster type</label>
            <select
              className={t.select}
              value={form.roster_type}
              disabled={!metadataEditable}
              onChange={(e) => setForm({ ...form, roster_type: e.target.value })}
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
        <div>
          <label className={t.label}>Unit / location</label>
          <input
            className={t.input}
            value={form.unit_label}
            disabled={!metadataEditable}
            onChange={(e) => setForm({ ...form, unit_label: e.target.value })}
            placeholder="Optional organisation unit"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button type="button" onClick={onClose} className={t.btnSecondary}>
            Cancel
          </button>
          {metadataEditable && (!duplicateRoster || isEdit) && (
            <button type="submit" disabled={saving} className={t.btnPrimary}>
              {saving ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save changes" : "Create roster"}
            </button>
          )}
        </div>
      </form>
    </ModalShell>
  );
}
